import { useCallback, useEffect, useRef, useState } from 'react'
import { useAppDispatch } from '@/utils/typeHelpers'
import { updateYourTurnCount } from '@/components/conversations/conversation.slice'
import { fetchMessageThreads } from '../adviser-chat.actions'
import type {
  MessageScope,
  MessageTab,
  MessageThread,
  MessageThreadCounts,
  MessageThreadCursor,
  MessageThreadsResponse,
} from '../lib/types'

const SEARCH_DEBOUNCE_MS = 300
const SETTLE_POLL_DELAYS_MS = [2000, 4000, 8000, 16000]

const FIRST_CURSOR: MessageThreadCursor = { page: 1, before: null }

const EMPTY_COUNTS: MessageThreadCounts = {
  awaiting: 0,
  withHeard: 0,
  resolved: 0,
}

interface OlderPages {
  threads: MessageThread[]
  nextCursor: MessageThreadCursor | null
}

const mergeThreads = (
  current: MessageThread[],
  incoming: MessageThread[]
): MessageThread[] => {
  const seen = new Set(current.map((t) => t.ticketId))
  return [...current, ...incoming.filter((t) => !seen.has(t.ticketId))]
}

interface UseMessageThreadsResult {
  threads: MessageThread[]
  counts: MessageThreadCounts
  hasHistory: boolean
  scope: MessageScope
  resolvedTab: MessageTab
  search: string
  setSearch: (value: string) => void
  selectTab: (tab: MessageTab) => void
  loadOlder: () => void
  hasMore: boolean
  loading: boolean
  loadingOlder: boolean
  error: boolean
  retry: () => void
}

export const useMessageThreads = (options?: {
  initialTab?: MessageTab
  initialQuery?: string
  initialCounts?: MessageThreadCounts
  isSettled?: (page: MessageThreadsResponse, tab: MessageTab) => boolean
}): UseMessageThreadsResult => {
  // requestedTab stays undefined until the user picks one, so the server keeps
  // deciding the default. Storing the server's answer here instead would make
  // the load effect refire and double every first paint.
  const dispatch = useAppDispatch()
  const [requestedTab, setRequestedTab] = useState<MessageTab | undefined>(
    options?.initialTab
  )
  const [search, setSearch] = useState(options?.initialQuery ?? '')
  const [debouncedSearch, setDebouncedSearch] = useState(
    options?.initialQuery ?? ''
  )
  const [initialPage, setInitialPage] = useState<MessageThreadsResponse | null>(
    null
  )
  const [olderPages, setOlderPages] = useState<OlderPages | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadingOlder, setLoadingOlder] = useState(false)
  const [error, setError] = useState(false)
  const [reloadToken, setReloadToken] = useState(0)

  // Bumped by anything that invalidates the current list. A response carrying a
  // stale generation is dropped, so a slow "load older" cannot append its rows
  // into a tab the user has since switched to.
  const generation = useRef(0)
  const silentReload = useRef(false)
  const isSettledRef = useRef(options?.isSettled)
  const pollAttempt = useRef(0)
  const pollTimer = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedSearch(search),
      SEARCH_DEBOUNCE_MS
    )
    return () => clearTimeout(timer)
  }, [search])

  useEffect(() => {
    const silent = silentReload.current
    silentReload.current = false
    const thisGeneration = silent ? generation.current : ++generation.current
    clearTimeout(pollTimer.current)
    if (!silent) {
      setLoading(true)
      setError(false)
      setLoadingOlder(false)
    }

    fetchMessageThreads({
      tab: requestedTab,
      search: debouncedSearch,
      cursor: FIRST_CURSOR,
    }).then((data) => {
      if (thisGeneration !== generation.current) return
      if (data) {
        setInitialPage(data)
        if (data.counts) dispatch(updateYourTurnCount(data.counts.awaiting))
        if (!silent) setOlderPages(null)
      } else if (!silent) {
        setError(true)
      }
      setLoading(false)

      const isSettled = isSettledRef.current
      if (!isSettled || (!data && !silent)) return
      if (data && isSettled(data, requestedTab ?? data.defaultTab)) return
      const delay = SETTLE_POLL_DELAYS_MS[pollAttempt.current]
      if (delay === undefined) return
      pollAttempt.current += 1
      pollTimer.current = setTimeout(() => {
        silentReload.current = true
        setReloadToken((t) => t + 1)
      }, delay)
    })
  }, [requestedTab, debouncedSearch, reloadToken, dispatch])

  // Unmounting invalidates the generation too: the count a late response
  // publishes outlives this list, and a newer one may have published already.
  useEffect(
    () => () => {
      generation.current += 1
      clearTimeout(pollTimer.current)
    },
    []
  )

  const resolvedTab = requestedTab ?? initialPage?.defaultTab ?? 'awaiting'
  const cursor = olderPages
    ? olderPages.nextCursor
    : (initialPage?.nextCursor ?? null)

  // A search spans every status, so picking a tab ends it.
  const selectTab = useCallback((tab: MessageTab) => {
    setRequestedTab(tab)
    setSearch('')
    setDebouncedSearch('')
  }, [])

  const loadOlder = useCallback(() => {
    if (cursor === null) return
    const thisGeneration = generation.current
    setLoadingOlder(true)

    fetchMessageThreads({
      tab: resolvedTab,
      search: debouncedSearch,
      cursor,
    }).then((data) => {
      if (thisGeneration !== generation.current) return
      setLoadingOlder(false)
      if (!data) return
      setOlderPages((current) => ({
        threads: mergeThreads(current?.threads ?? [], data.threads),
        nextCursor: data.nextCursor,
      }))
    })
  }, [cursor, resolvedTab, debouncedSearch])

  const retry = useCallback(() => setReloadToken((t) => t + 1), [])

  return {
    threads: mergeThreads(
      initialPage?.threads ?? [],
      olderPages?.threads ?? []
    ),
    counts: initialPage?.counts ?? options?.initialCounts ?? EMPTY_COUNTS,
    hasHistory: initialPage?.hasHistory ?? false,
    scope: initialPage?.scope ?? 'tab',
    resolvedTab,
    search,
    setSearch,
    selectTab,
    loadOlder,
    hasMore: cursor !== null,
    loading,
    loadingOlder,
    error,
    retry,
  }
}
